/**
 * #1499 — Persist boot sign-in hold across WebView reloads (connector update / bundle swap).
 * Until the user completes sign-in, automatic session restore and boot spinner must not return.
 */

import { forceBootTerminalSignInLatch, isBootTimeTerminalRejection } from './authTerminalLatch';
import { markBootSurrendered, isBootSurrendered } from './bootDeadline';

const SS_HOLD_SIGNIN = 'maestro_boot_hold_signin_v1';
const SS_HOLD_REASON = 'maestro_boot_hold_signin_reason_v1';

export type BootHoldReason = 'dead_refresh' | 'boot_surrender' | 'boot_terminal';

export function isBootHoldSignInActive(): boolean {
  try {
    return sessionStorage.getItem(SS_HOLD_SIGNIN) === '1';
  } catch {
    return false;
  }
}

export function persistBootHoldSignIn(reason: BootHoldReason): void {
  try {
    sessionStorage.setItem(SS_HOLD_SIGNIN, '1');
    sessionStorage.setItem(SS_HOLD_REASON, reason);
  } catch { /* ignore */ }
}

export function clearBootHoldSignIn(): void {
  try {
    sessionStorage.removeItem(SS_HOLD_SIGNIN);
    sessionStorage.removeItem(SS_HOLD_REASON);
  } catch { /* ignore */ }
}

/** Re-apply in-memory gates after a WebView reload wiped module state. */
export function hydrateBootHoldSignInFromSession(): boolean {
  if (!isBootHoldSignInActive()) return false;
  let reason: BootHoldReason = 'dead_refresh';
  try {
    const raw = sessionStorage.getItem(SS_HOLD_REASON);
    if (raw === 'boot_surrender' || raw === 'boot_terminal' || raw === 'dead_refresh') {
      reason = raw;
    }
  } catch { /* ignore */ }
  if (reason === 'boot_terminal' || reason === 'dead_refresh') {
    forceBootTerminalSignInLatch('reauth_required');
  } else {
    markBootSurrendered(false);
  }
  return true;
}

/** True when automatic refresh / auth.json restore must not run. */
export function shouldBlockAutomaticSessionRestore(): boolean {
  return isBootHoldSignInActive()
    || isBootTimeTerminalRejection()
    || isBootSurrendered();
}

/** Best-effort disk clear so a fresh sign-in is the only recovery path (customer flow). */
export async function clearConnectorCredentialsForDeadSession(caller: string): Promise<void> {
  if (typeof window === 'undefined') return;
  const invoke = (window as Window & { __TAURI__?: { core?: { invoke?: (cmd: string) => Promise<unknown> } } })
    .__TAURI__?.core?.invoke;
  if (!invoke) return;
  try {
    await invoke('clear_credentials');
    console.warn(`[Auth:lifecycle] clear_credentials after dead refresh caller=${caller}`);
  } catch (e) {
    console.warn('[Auth:lifecycle] clear_credentials failed (sign-in may still seed auth.json):', e);
  }
}

export function resetBootHoldSignInForTests(): void {
  clearBootHoldSignIn();
}
